import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Claim } from '@wsphere/warranties/domain';
import { ClaimResolutionOptionsService } from '@wsphere/warranties/web';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-claim-resolution-settings',
  templateUrl: './claim-resolution-settings.component.html',
  standalone: false,
})
export class ClaimResolutionSettingsComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private toastSvc: ToastService,
    private claimResolutionOptionsSvc: ClaimResolutionOptionsService,
    private cdr: ChangeDetectorRef
  ) {}

  @Input() canEdit = false;

  protected loading = true;
  protected saving = false;
  protected readonly maxLength = Claim.CLAIM_RESOLUTION_MAX_LENGTH;

  protected form = this.formBuilder.group({
    values: this.formBuilder.array<FormControl<string | null>>([]),
  });

  get values() {
    return this.form.controls.values as FormArray<FormControl<string | null>>;
  }

  get hasDuplicateValues() {
    const enteredValues = this.values
      .getRawValue()
      .map(value => Claim.normalizeClaimResolutionValue(value))
      .filter((value): value is string => !!value);

    return new Set(enteredValues).size !== enteredValues.length;
  }

  ngOnInit(): void {
    void this.load();
  }

  protected addOption(value = '') {
    this.values.push(this.formBuilder.control(value, [Validators.maxLength(this.maxLength)]));
    this.form.markAsDirty();
  }

  protected removeOption(index: number) {
    this.values.removeAt(index);

    if (!this.values.length) {
      this.values.push(this.formBuilder.control('', [Validators.maxLength(this.maxLength)]));
    }

    this.form.markAsDirty();
  }

  protected async reset() {
    await this.load();
  }

  protected async save() {
    if (this.form.invalid || this.hasDuplicateValues) return;

    this.saving = true;

    const values = Claim.normalizeClaimResolutionOptions(this.values.getRawValue());
    const response = await this.claimResolutionOptionsSvc.updateOptions({ values }).catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: error.message,
      });

      return null;
    });

    if (response) {
      this.toastSvc.show({
        type: 'success',
        title: 'Claim resolutions updated',
        text: 'Claim resolution options have been successfully updated for your organization.',
      });

      await this.load();
    }

    this.saving = false;
    this.cdr.markForCheck();
  }

  private async load() {
    this.loading = true;

    const values = await this.claimResolutionOptionsSvc.listOptions().catch(({ error }) => {
      this.toastSvc.show({
        type: 'error',
        title: 'Failed to load claim resolutions',
        text: error.message,
      });

      return [] as string[];
    });

    this.values.clear();
    for (const value of values) {
      this.values.push(this.formBuilder.control(value, [Validators.maxLength(this.maxLength)]));
    }

    if (!this.values.length) {
      this.values.push(this.formBuilder.control('', [Validators.maxLength(this.maxLength)]));
    }

    this.form.markAsPristine();
    // Set after awaits, so the view needs marking or the loading text never clears.
    this.loading = false;
    this.cdr.markForCheck();
  }
}
